import { useEffect, useRef } from "react";
import { X, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FundioLogo } from "@/components/FundioLogo";
import { useAuth } from "@/context/AuthContext";
import { NavGroups } from "@/components/layout/Sidebar";
import { LangMenu, initialsOf, useLogout } from "@/components/layout/ShellMenus";
import { StatusSegments } from "@/components/layout/StatusBadge52";
import { useSystemStatus } from "@/lib/systemStatus";
import { useT52 } from "@/i18n/v52";

const FOCUSABLE = 'a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])';

// Mobile-Navigation: Fokus im Drawer, Tab-Falle, Esc schliesst, Fokus zurück zum Menüknopf, kein Scrollen dahinter.
export function MobileDrawer({ open, onClose, returnFocusRef }) {
  const ref = useRef(null);
  const { user } = useAuth();
  const { t } = useTranslation();
  const { t52 } = useT52();
  const st = useSystemStatus();
  const [logout, busy] = useLogout();

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current?.querySelector("[data-drawer-close]")?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab" || !ref.current) return;
      const f = [...ref.current.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    const back = returnFocusRef.current;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      if (back && back.offsetParent !== null) back.focus();
    };
  }, [open, onClose, returnFocusRef]);

  if (!open) return null;
  const openStatus = () => { onClose(); st.check(); st.setPanelOpen(true); };
  return (
    <>
      <div className="f52-backdrop" onClick={onClose} data-testid="mobile-nav-backdrop" aria-hidden="true" />
      <div id="control-drawer" ref={ref} className="f52-drawer" role="dialog" aria-modal="true" aria-label={t52("navigation")} data-testid="mobile-nav-drawer">
        <div className="f52-drawer-head">
          <FundioLogo variant="weiss" height={26} />
          <button type="button" className="f52-icon-btn" onClick={onClose} aria-label={t52("closeMenu")} data-drawer-close data-testid="mobile-nav-close"><X aria-hidden="true" /></button>
        </div>
        <div className="f52-drawer-user">
          <span className="f52-avatar" aria-hidden="true">{initialsOf(user)}</span>
          <div><div className="nm">{user?.name}</div><div className="rl">{user?.role_label}</div></div>
        </div>
        <button type="button" className="f52-drawer-status" onClick={openStatus} data-testid="mobile-nav-status">
          {t52("status")}<span className="segs"><StatusSegments /></span>
        </button>
        <nav className="f52-nav" aria-label={t52("mainNav")}><NavGroups prefix="drawer-" onNavigate={onClose} /></nav>
        <div className="f52-drawer-foot">
          <LangMenu testid="lang-switcher-drawer" side="top" />
          <button type="button" className="f52-logout" onClick={logout} disabled={busy} data-testid="mobile-nav-logout"><LogOut aria-hidden="true" />{t("layout.logout")}</button>
        </div>
      </div>
    </>
  );
}
